import React, { useState, useEffect } from "react";
import { CodeSnippetView } from "./CodeSnippetView";
import { resolveNotebookUrl } from "./notebookUrls";
import { useApiKey } from "../../../hooks/useApiKey";
import { InlineLoadingSpinner } from "../../LoadingSpinner";

/**
 * Code snippet modal body for a completed exportDataset task.
 *
 * Rendered inside the notification system's `showModal` — mounts in the
 * NotificationProvider tree, so hooks work normally. Fetches apiKey via
 * `useApiKey` at render time rather than storing it at dispatch time.
 */
export function ExportDatasetSnippetContent({
    downloadUrl,
    format,
    datasetType,
    workspaceUrl,
    datasetName,
    onClose
}: {
    downloadUrl: string;
    format: string;
    datasetType?: string;
    workspaceUrl: string;
    datasetName: string;
    onClose: () => void;
}) {
    const apiKey = useApiKey();
    const [timedOut, setTimedOut] = useState(false);

    useEffect(() => {
        if (apiKey) return;
        const id = setTimeout(() => setTimedOut(true), 3000);
        return () => clearTimeout(id);
    }, [apiKey]);

    const loading = !apiKey && !timedOut;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col items-center gap-1 text-center">
                <i className="far fa-check-circle text-3xl text-green-500" />
                <span className="text-sm font-medium text-gray-800">
                    Your {format.toUpperCase()} export is ready.
                </span>
                <span className="text-xs text-gray-500">
                    Use the snippets below to integrate it into your workflow.
                </span>
            </div>
            {loading ? (
                <InlineLoadingSpinner className="py-8" />
            ) : (
                <CodeSnippetView
                    downloadUrl={downloadUrl}
                    format={format}
                    workspaceUrl={workspaceUrl}
                    datasetName={datasetName}
                    apiKey={apiKey}
                    notebookUrl={resolveNotebookUrl(format, datasetType)}
                    onClose={onClose}
                />
            )}
        </div>
    );
}
